import { motion, AnimatePresence } from "motion/react";
import { Check, Circle } from "lucide-react";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

interface AgentStep {
  step: number;
  phase: string;
  reasoningText: string;
  reasoningActive: boolean;
  toolCallIds?: string[];
}

interface AgentProgressProps {
  steps: AgentStep[];
  currentStep: number;
  isComplete: boolean;
}

export function AgentProgress({ steps, currentStep, isComplete }: AgentProgressProps) {
  const { t } = useTranslation();

  const phaseLabels: Record<string, string> = {
    thinking: t('thinking'),
    calling_llm: t('calling_llm'),
    callingLlm: t('callingLlm'),
    generating: t('generating'),
    generating_image: t('generating_image'),
    processing: t('processing'),
    tool_running: t('tool_running'),
    toolRunning: t('toolRunning'),
    executing_tools: t('executing_tools'),
    executingTools: t('executingTools'),
    running: t('running'),
  };

  if (!steps || steps.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
        <motion.div
          className="w-4 h-4 rounded-full border-2 border-accent/30 border-t-accent"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
        />
        <span>{t('processing')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 mb-3">
      {steps.map((step, index) => {
        const isDone = isComplete || (step.step < currentStep);
        const isActive = !isComplete && step.step === currentStep;
        const showReasoning = step.reasoningActive && step.reasoningText.length > 0;

        return (
          <div key={index} className="flex flex-col">
            <div className="flex items-center gap-2">
              {/* Иконка состояния */}
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                {isDone ? (
                  <Check size={14} className="text-emerald-500" />
                ) : isActive ? (
                  <motion.div
                    className="w-3.5 h-3.5 rounded-full border-2 border-accent/30 border-t-accent"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                  />
                ) : (
                  <Circle size={12} className="text-white/20" />
                )}
              </div>
              <span
                className={`text-sm ${
                  isDone
                    ? 'text-emerald-500 font-medium'
                    : isActive
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground'
                }`}
              >
                {phaseLabels[step.phase] || step.phase}
              </span>
              {step.toolCallIds && step.toolCallIds.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {step.toolCallIds.length} tools
                </span>
              )}
            </div>

            {/* Reasoning раскрывается только когда активен */}
            <AnimatePresence>
              {showReasoning && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden ml-6 pl-2 border-l border-white/10"
                >
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">
                    {step.reasoningText}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}